import { ReloadOutlined } from '@ant-design/icons';
import {
  ApiError,
  CachePolicy,
  type CategoryMetricRow,
  type MaterialMetricRow,
  type OrgMetricRow
} from '@hq/api-sdk';
import { formatAmount, formatQty } from '@hq/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Progress, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { hq } from '../session';
import { TipText, sumWidth } from '@hq/ui-kit';

const columns: ColumnsType<OrgMetricRow> = [
  {
    title: '机构',
    dataIndex: 'orgName',
    width: 220,
    ellipsis: { showTitle: false },
    render: (value: string | null, row) => <TipText text={value ?? `机构 ${row.orgId}`} />
  },
  { title: '配送单量', dataIndex: 'deliveryCount', width: 120, align: 'right' },
  {
    title: '签收金额',
    dataIndex: 'signedAmount',
    width: 160,
    align: 'right',
    render: (value: number) => formatAmount(value)
  },
  { title: '退货单量', dataIndex: 'returnCount', width: 110, align: 'right' },
  {
    title: '退货金额',
    dataIndex: 'returnAmount',
    width: 150,
    align: 'right',
    render: (value: number) => <Typography.Text type="danger">{formatAmount(value)}</Typography.Text>
  },
  {
    title: '退货率',
    key: 'returnRate',
    width: 110,
    align: 'right',
    render: (_v, row) => {
      const rate = row.deliveryCount > 0 ? row.returnCount / row.deliveryCount : 0;
      return `${(rate * 100).toFixed(1)}%`;
    }
  },
  {
    title: '健康度',
    key: 'health',
    width: 110,
    render: (_v, row) => {
      // 口径：退货率 ≤5% 绿、≤15% 黄、其余红；无单量不评价
      if (row.deliveryCount === 0) return <Tag>无数据</Tag>;
      const rate = row.returnCount / row.deliveryCount;
      if (rate <= 0.05) return <Tag color="green">健康</Tag>;
      if (rate <= 0.15) return <Tag color="orange">关注</Tag>;
      return <Tag color="red">异常</Tag>;
    }
  }
];

/** 运营看板：实时读取 `/api/wl/analytics`，跨机构需登录 level=admin。 */
export default function Dashboard() {
  const overview = useQuery({
    queryKey: ['ops', 'analytics', 'overview'],
    placeholderData: keepPreviousData,
    staleTime: CachePolicy.aggregate,
    queryFn: () => hq.analytics.overview()
  });

  const orgs = useQuery({
    queryKey: ['ops', 'analytics', 'orgs'],
    placeholderData: keepPreviousData,
    staleTime: CachePolicy.aggregate,
    // 运营商向卫健局汇报需要逐家清单，不再只取 TOP 8
    queryFn: () => hq.analytics.orgs({ limit: 40 })
  });

  const categories = useQuery({
    queryKey: ['ops', 'analytics', 'categories'],
    placeholderData: keepPreviousData,
    staleTime: CachePolicy.aggregate,
    queryFn: () => hq.analytics.categories({ limit: 8 })
  });

  const materials = useQuery({
    queryKey: ['ops', 'analytics', 'materials'],
    placeholderData: keepPreviousData,
    staleTime: CachePolicy.aggregate,
    queryFn: () => hq.analytics.materials({ limit: 8 })
  });

  const data = overview.data;
  const error = overview.error instanceof ApiError ? overview.error.friendlyMessage : null;

  return (
    <>
      {error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message="统计加载失败" description={error} /> : null}

      <Card
        className="page-card"
        title={
          <Space>
            履约健康度
            <Typography.Text type="secondary">本月实时</Typography.Text>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            loading={overview.isFetching || orgs.isFetching || categories.isFetching || materials.isFetching}
            onClick={() => {
              void overview.refetch();
              void orgs.refetch();
              void categories.refetch();
              void materials.refetch();
            }}
          >
            刷新
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} xl={6}>
            <Statistic title="申领单量" value={data?.applyCount ?? 0} suffix="单" loading={overview.isPending} />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic title="配送单量" value={data?.deliveryCount ?? 0} suffix="单" loading={overview.isPending} />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="已签收金额"
              value={data?.signedAmount ?? 0}
              precision={2}
              suffix="元"
              loading={overview.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="配送准时率"
              value={data?.onTimeRate ?? 0}
              precision={1}
              suffix="%"
              valueStyle={{ color: (data?.onTimeRate ?? 0) >= 95 ? '#3f8600' : '#d46b08' }}
              loading={overview.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="退货单量"
              value={data?.returnCount ?? 0}
              suffix="单"
              valueStyle={{ color: '#cf1322' }}
              loading={overview.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="退货金额"
              value={formatAmount(data?.returnAmount ?? 0)}
              suffix="元"
              valueStyle={{ color: '#cf1322' }}
              loading={overview.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="对账应付"
              value={data?.settlePayable ?? 0}
              precision={2}
              suffix="元"
              loading={overview.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="已付款"
              value={data?.settlePaid ?? 0}
              precision={2}
              suffix="元"
              valueStyle={{ color: '#3f8600' }}
              loading={overview.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic
              title="在库金额"
              value={data?.stockAmount ?? 0}
              precision={2}
              suffix="元"
              loading={overview.isPending}
            />
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Statistic title="在库 SKU" value={data?.skuCount ?? 0} suffix="个" loading={overview.isPending} />
          </Col>
        </Row>

        {data ? (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Card size="small" title="签收完成率">
                <Progress
                  percent={
                    data.deliveryCount > 0 ? Math.round((data.signedCount / data.deliveryCount) * 100) : 0
                  }
                  status="active"
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="待付款占比">
                <Progress
                  percent={
                    data.settlePayable > 0 ? Math.round((data.settlePaid / data.settlePayable) * 100) : 0
                  }
                  status="success"
                />
              </Card>
            </Col>
          </Row>
        ) : null}
      </Card>

      <Card
        className="page-card"
        title={
          <Space>
            机构健康度清单（本月）
            <Tag color="blue">实时</Tag>
            <Typography.Text type="secondary">{orgs.data?.length ?? 0} 家</Typography.Text>
          </Space>
        }
      >
        {orgs.isError ? (
          <Alert
            type="error"
            showIcon
            message="机构健康度加载失败"
            description={orgs.error instanceof ApiError ? orgs.error.friendlyMessage : String(orgs.error)}
          />
        ) : null}
        <Table<OrgMetricRow>
          rowKey="orgId"
          size="middle"
          columns={columns}
          dataSource={orgs.data ?? []}
          loading={orgs.isPending}
          pagination={{ pageSize: 16, hideOnSinglePage: true, showSizeChanger: false }}
          scroll={{ x: sumWidth(columns) }}
          locale={{ emptyText: '本月暂无配送数据' }}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card className="page-card" title="品类结构（本月 TOP 8）">
            {categories.isError ? (
              <Alert
                type="error"
                showIcon
                message="品类统计加载失败"
                description={
                  categories.error instanceof ApiError ? categories.error.friendlyMessage : String(categories.error)
                }
              />
            ) : null}
            <Table<CategoryMetricRow>
              rowKey="categoryId"
              size="middle"
              pagination={false}
              loading={categories.isPending}
              dataSource={categories.data ?? []}
              locale={{ emptyText: '本月暂无品类数据' }}
              columns={[
                {
                  title: '品类',
                  dataIndex: 'categoryName',
                  ellipsis: { showTitle: false },
                  render: (value: string | null, row) => <TipText text={value ?? `品类 ${row.categoryId}`} />
                },
                { title: '数量', dataIndex: 'qty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
                { title: '金额', dataIndex: 'amount', width: 150, align: 'right', render: (value: number) => formatAmount(value) }
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card className="page-card" title="物资消耗（本月 TOP 8）">
            {materials.isError ? (
              <Alert
                type="error"
                showIcon
                message="物资统计加载失败"
                description={
                  materials.error instanceof ApiError ? materials.error.friendlyMessage : String(materials.error)
                }
              />
            ) : null}
            <Table<MaterialMetricRow>
              rowKey="materialId"
              size="middle"
              pagination={false}
              loading={materials.isPending}
              dataSource={materials.data ?? []}
              locale={{ emptyText: '本月暂无物资消耗数据' }}
              columns={[
                {
                  title: '物资',
                  dataIndex: 'materialName',
                  ellipsis: { showTitle: false },
                  render: (value: string | null, row) => (
                    <TipText text={`${value ?? `物资 ${row.materialId}`}${row.materialCode ? `（${row.materialCode}）` : ''}`} />
                  )
                },
                { title: '数量', dataIndex: 'qty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
                { title: '金额', dataIndex: 'amount', width: 150, align: 'right', render: (value: number) => formatAmount(value) }
              ]}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}
